import { onSectionReorder } from '@griply/utils';
import { useUpdateTasks } from 'features/tasks';
import React, { useCallback, useMemo, useRef } from 'react';
import {
  rootId,
  TaskListItemTypes,
  TreeItem,
  useTree,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import { useTaskDateShortcuts } from 'shared/hooks/use-task-date-shortcuts';
import { useTaskPriorityShortcuts } from 'shared/hooks/use-task-priority-shortcuts';
import { ID } from 'shared/types/id';
import { Task } from 'shared/types/task';
import { focusOnEndOfEditableContent } from 'shared/utils/focus-on-end-of-element';

import { useSectionTree } from '../../hooks/use-section-tree';
import { GroupedListProps, noGroupingId } from '../../types';
import * as Styled from './section-grouping-tasks-list.style';
import {
  SectionRenderData,
  SectionRenderDataContext,
} from './section-render-context';
import { SectionRenderItem } from './section-render-item';

const taskItemTypes = [TaskListItemTypes.Task, TaskListItemTypes.Section];

const isItemDraggable = (item: TreeItem) => item.id !== noGroupingId;

export const SectionGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
  denseFormat,
  groupSettings,
  isContextMenuOpen,
}) => {
  const dropTypes: Record<TaskListItemTypes, TaskListItemTypes[]> = useMemo(
    () => ({
      [TaskListItemTypes.Root]: [
        TaskListItemTypes.Section,
        TaskListItemTypes.Task,
      ],
      [TaskListItemTypes.Section]: [TaskListItemTypes.Task],
      [TaskListItemTypes.Task]: [],
      [TaskListItemTypes.NewTask]: [],
      [TaskListItemTypes.Goal]: [],
      [TaskListItemTypes.GoalDeadline]: [],
      [TaskListItemTypes.LifeArea]: [],
      [TaskListItemTypes.Priority]: [],
      [TaskListItemTypes.Date]: [],
      [TaskListItemTypes.DayPart]: [],
      [TaskListItemTypes.Tag]: [],
      [TaskListItemTypes.Type]: [],
    }),
    [],
  );

  const { sections = [], showEmptyGroups = true } = groupSettings;
  const today = useToday();

  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const treeData = useSectionTree({
    tasks,
    sections,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
    showEmptyGroups,
    hideNewTaskRow: groupSettings.hideNewTaskRow,
  });

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const result = onSectionReorder({
      updatedItems,
      hasSections: !!sections?.length,
      rootSectionId: rootId,
    });

    onReorder(result.updatedSections, result.updatedTasks);
  };

  // Map of section IDs to their new task input elements
  const newTaskInputRefs = useRef<Record<ID, HTMLDivElement | null>>({});

  const registerNewTaskInput = useCallback(
    (sectionId: ID, element: HTMLDivElement | null) => {
      newTaskInputRefs.current[sectionId] = element;
    },
    [],
  );

  const onAddTask = useCallback((sectionId: ID) => {
    const input = newTaskInputRefs.current[sectionId];
    if (!input) {
      return;
    }

    requestAnimationFrame(() => focusOnEndOfEditableContent(input));
  }, []);

  const renderData = useMemo<SectionRenderData>(
    () => ({
      groupSettings,
      tasksMap,
      tasks,
      today,
      denseFormat,
      registerNewTaskInput,
      onAddTask,
    }),
    [
      groupSettings,
      tasksMap,
      tasks,
      today,
      denseFormat,
      registerNewTaskInput,
      onAddTask,
    ],
  );

  const [tree] = useTree();
  const { submit: updateTasks } = useUpdateTasks();

  useTaskPriorityShortcuts(tree, tasks, updateTasks);
  useTaskDateShortcuts(tree, tasks, updateTasks);

  return (
    <Styled.Container>
      <SectionRenderDataContext.Provider value={renderData}>
        <VirtualizedTreeList
          rootItemId={rootId}
          items={treeData}
          renderItem={SectionRenderItem}
          scrollElement={scrollElement}
          multiselectTypes={taskItemTypes}
          dragTypes={taskItemTypes}
          onReorder={_onReorder}
          isItemDraggable={isItemDraggable}
          collapsedItems={collapsedSections}
          onCollapse={onSectionCollapse}
          denseFormat={denseFormat}
          isContextMenuOpen={isContextMenuOpen}
          dropTypes={dropTypes}
        />
      </SectionRenderDataContext.Provider>
    </Styled.Container>
  );
};
